import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import type { AppState, AppThunk } from "@Store/index";
import { CmsProduct, fetchUniKitProducts } from "../services/unikitCms";

type ReadyStatus = "invalid" | "request" | "success" | "failure";

export interface CategoryListingState {
  status: ReadyStatus;
  products: CmsProduct[];
  error: string | null;
}

interface EssentialKitListingState {
  /** Listing products keyed by category slug so each category is cached independently. */
  byCategory: Record<string, CategoryListingState>;
}

const initialState: EssentialKitListingState = {
  byCategory: {},
};

const listingSlice = createSlice({
  name: "essentialKitListing",
  initialState,
  reducers: {
    listingRequesting(state, { payload: categorySlug }: PayloadAction<string>) {
      state.byCategory[categorySlug] = {
        status: "request",
        products: state.byCategory[categorySlug]?.products ?? [],
        error: null,
      };
    },
    listingSuccess(
      state,
      { payload }: PayloadAction<{ categorySlug: string; products: CmsProduct[] }>,
    ) {
      state.byCategory[payload.categorySlug] = {
        status: "success",
        products: payload.products,
        error: null,
      };
    },
    listingFailure(state, { payload }: PayloadAction<{ categorySlug: string; error: string }>) {
      state.byCategory[payload.categorySlug] = {
        status: "failure",
        products: [],
        error: payload.error,
      };
    },
  },
});

export const { listingRequesting, listingSuccess, listingFailure } = listingSlice.actions;

/** Read the listing slice for a single category (undefined until first fetch). */
export const selectCategoryListing = (
  state: AppState,
  categorySlug: string,
): CategoryListingState | undefined => state.essentialKitListing.byCategory[categorySlug];

const shouldFetch = (state: AppState, categorySlug: string): boolean => {
  const status = state.essentialKitListing.byCategory[categorySlug]?.status;
  // Fetch on first load and retry after a previous failure.
  return status === undefined || status === "invalid" || status === "failure";
};

const fetchListingProducts =
  (categorySlug: string): AppThunk =>
  async (dispatch) => {
    dispatch(listingRequesting(categorySlug));
    try {
      const products = await fetchUniKitProducts({
        categorySlug,
        onlyAvailable: true,
        // Skip the module-level promise cache on the server so long-lived Node
        // processes don't serve stale listings; keep it on the client.
        useCache: !__SERVER__,
      });
      dispatch(listingSuccess({ categorySlug, products }));
    } catch (err: any) {
      dispatch(listingFailure({ categorySlug, error: err?.message || "Failed to load kits" }));
    }
  };

/**
 * Fetches listing products for a category unless they're already loaded.
 * Used by SSR `loadData` and by the Listing component on client navigation.
 */
export const fetchListingProductsIfNeeded =
  (categorySlug: string): AppThunk =>
  (dispatch, getState) => {
    if (!categorySlug) return null;
    if (!shouldFetch(getState(), categorySlug)) return null;
    return dispatch(fetchListingProducts(categorySlug));
  };

export default listingSlice.reducer;
